import React from 'react';
import { SETTING } from 'interfaces/settings';
import { Typography } from 'interfaces/typography';
import { useAutodialerSettings } from '../hooks/useAutodialerSettings';
import { Users, Building2, User } from 'lucide-react';
import { SelectionType } from "interfaces/settings";
import { useUserData } from '../../../lib/redux/users/user-data-hook';

interface DifferentUserSettingDisplayProps {
    setting: SETTING;
    isInMultiRow?: boolean;
}

export function DifferentUserSettingDisplay({ setting, isInMultiRow = false }: DifferentUserSettingDisplayProps) {
    const { getValueForSetting, getSettingSetBySelection, formatSettingValue } = useAutodialerSettings();
    const { sortedUserGroupInfo: subgroups, allUsers } = useUserData();
    const settingSetBySelection = getSettingSetBySelection(setting);
    const value = getValueForSetting(setting)

    const getIconLabelAndColors = () => {
        switch (settingSetBySelection?.type) {
        case SelectionType.USER:
            return { 
                icon: <User size={16} />, 
                label: 'Set by user', 
                bgColor: 'bg-green-50', 
                borderColor: 'border-green-200', 
                iconColor: 'text-green-600', 
                valueColor: '#059669', 
                labelColor: '#065f46',
                labelBgColor: 'bg-green-100' 
            };
        case SelectionType.SUB_TEAM:
            const subgroup = subgroups.find(g => g.group.user_group_id === settingSetBySelection.id);
            if (subgroup) return { 
                icon: <Users size={16} />, 
                label: `Set by subteam - ${subgroup.group.user_group_name}`, 
                bgColor: 'bg-purple-50', 
                borderColor: 'border-purple-200', 
                iconColor: 'text-purple-600', 
                valueColor: '#7c3aed', 
                labelColor: '#581c87',
                labelBgColor: 'bg-purple-100' 
            };
            return { 
                icon: <Users size={16} />, 
                label: 'Set by subteam', 
                bgColor: 'bg-purple-50', 
                borderColor: 'border-purple-200', 
                iconColor: 'text-purple-600', 
                valueColor: '#7c3aed', 
                labelColor: '#581c87',
                labelBgColor: 'bg-purple-100' 
            };
        case SelectionType.OVERALL_TEAM:
            return { 
                icon: <Building2 size={16} />, 
                label: 'Set by team', 
                bgColor: 'bg-blue-50', 
                borderColor: 'border-blue-200', 
                iconColor: 'text-blue-600', 
                valueColor: '#2563eb', 
                labelColor: '#1e3a8a',
                labelBgColor: 'bg-blue-100' 
            };
        case SelectionType.ME:
            const user = allUsers?.find(u => u.user_id === settingSetBySelection.id)
            if (user) return { 
                icon: <User size={16} />, 
                label: `Set by ${user.user_name}`, 
                bgColor: 'bg-green-50', 
                borderColor: 'border-green-200', 
                iconColor: 'text-green-600', 
                valueColor: '#059669', 
                labelColor: '#065f46',
                labelBgColor: 'bg-green-100' 
            };
            return { 
                icon: <User size={16} />, 
                label: 'Set by user', 
                bgColor: 'bg-green-50', 
                borderColor: 'border-green-200', 
                iconColor: 'text-green-600', 
                valueColor: '#059669', 
                labelColor: '#065f46',
                labelBgColor: 'bg-green-100' 
            };
        default:
            return { 
                icon: <User size={16} />, 
                label: 'Set by other', 
                bgColor: 'bg-gray-50', 
                borderColor: 'border-gray-200', 
                iconColor: 'text-gray-600', 
                valueColor: '#4b5563', 
                labelColor: '#374151',
                labelBgColor: 'bg-gray-100' 
            };
        }
    }
    const { icon, label, bgColor, borderColor, iconColor, valueColor, labelColor, labelBgColor } = getIconLabelAndColors()

    // Compact version for multi-row
    if (isInMultiRow) {
        return (
            <div className="flex-1 h-full">
                <div className={`flex items-center justify-center gap-1 ${bgColor} px-2 py-1 rounded border ${borderColor} h-full`}>
                    <div className={iconColor}>
                        {React.cloneElement(icon, { size: 12 })}
                    </div>
                    <Typography variant="smallParagraph" color={valueColor} fontWeight="500">
                        {formatSettingValue(setting, value)}
                    </Typography>
                </div>
            </div>
        );
    }

    return (
        <div className={`flex items-center gap-2 ${bgColor} px-2 py-1 rounded border ${borderColor} flex-1 min-h-0`}>
            <div className={`${iconColor} flex-shrink-0`}>
                {icon}
            </div>
            <Typography variant="smallParagraph" color={valueColor} fontWeight="500">
                {formatSettingValue(setting, value)}
            </Typography>
            <Typography variant="smallParagraph" color={labelColor} fontWeight="500" className={`px-1.5 py-0.5 ${labelBgColor} rounded flex-shrink-0`}>
                {label}
            </Typography>
        </div>
    );
} 